import {
  FetchSearchCoursesPageableQuery,
  FetchSearchCoursesPageableQueryVariables,
  FetchSearchLatestResourcesPageableQuery,
  FetchSearchLatestResourcesPageableQueryVariables,
  FetchSearchPodcastsPageableQuery,
  FetchSearchPodcastsPageableQueryVariables,
  CourseEntity,
  PodcastEntity,
  ResourceEntity,
  FetchSearchPageQuery,
  FetchSearchPageQueryVariables,
  SearchPage,
} from './../../graphql/generated/graphql';
import { initializeApollo } from '../lib/apollo-client';
import requestHandler from '../requestHandler';
import {
  QUERY_FETCH_PODCASTS_PAGEABLE,
  QUERY_FETCH_RESOURCES_PAGEABLE,
  QUERY_FETCH_SEARCH_COURSE_PAGEABLE,
  QUERY_FETCH_SEARCH_COURSE_PREDICT,
  QUERY_FETCH_RESOURCES_PREDICT,
  QUERY_FETCH_PODCASTS_PREDICT,
  QUERY_SEARCH_FETCH_PAGE,
} from './../../graphql/queries/search/search';
import { getAuthentication } from '@/hooks/useAuthentication';
import { KNOWLEDGE_HUB_NAME, LEARN_NAME, PODCAST_NAME } from '@/pages/search/hooks/useSeach';

export type SearchPageableResult = {
  data: (CourseEntity | ResourceEntity | PodcastEntity)[];
  pagination: {
    page: number;
    pageCount: number;
  };
  hasMore: {
    [x: string]: boolean;
  };
};

export const fetchSearchPageable = async (page: number, filters: { search: string }) => {
  const { search } = filters;

  const apolloClient = initializeApollo();

  const [coursesResponse, resourcesResponse, podcastResponse] = await Promise.all([
    requestHandler(
      apolloClient.query<FetchSearchCoursesPageableQuery, FetchSearchCoursesPageableQueryVariables>({
        query: QUERY_FETCH_SEARCH_COURSE_PAGEABLE,
        fetchPolicy: 'cache-first',
        variables: {
          page,
          search,
        },
      }),
    ),
    requestHandler(
      apolloClient.query<FetchSearchLatestResourcesPageableQuery, FetchSearchLatestResourcesPageableQueryVariables>({
        query: QUERY_FETCH_RESOURCES_PAGEABLE,
        fetchPolicy: 'cache-first',
        variables: {
          page,
          search,
        },
      }),
    ),
    requestHandler(
      apolloClient.query<FetchSearchPodcastsPageableQuery, FetchSearchPodcastsPageableQueryVariables>({
        query: QUERY_FETCH_PODCASTS_PAGEABLE,
        fetchPolicy: 'cache-first',
        variables: {
          page,
          userId: `${getAuthentication()?.user?.id}`,
          search,
        },
      }),
    ),
  ]);

  const coursesPageCount = coursesResponse.data?.courses?.meta.pagination.pageCount || 0;
  const resourcesPageCount = resourcesResponse.data?.resources?.meta.pagination.pageCount || 0;
  const podcastPageCount = podcastResponse.data?.podcasts?.meta.pagination.pageCount || 0;
  const maxPageCount = Math.max(coursesPageCount, resourcesPageCount, podcastPageCount);

  const pagination = {
    page: page,
    pageCount: maxPageCount,
  };

  const result = {
    data: [
      ...(coursesResponse?.data?.courses?.data || []),
      ...(resourcesResponse?.data?.resources?.data || []),
      ...(podcastResponse?.data?.podcasts?.data || []),
    ],
    hasMore: {},
    pagination: pagination,
  } as SearchPageableResult;

  result.hasMore[LEARN_NAME] = coursesPageCount > page;
  result.hasMore[KNOWLEDGE_HUB_NAME] = resourcesPageCount > page;
  result.hasMore[PODCAST_NAME] = podcastPageCount > page;

  return result as SearchPageableResult;
};

export const fetchSearchPredict = async (filters: { search: string }) => {
  const apolloClient = initializeApollo();

  const { search } = filters;

  const [coursesResponse, resourcesResponse, podcastResponse] = await Promise.all([
    requestHandler(
      apolloClient.query<FetchSearchCoursesPageableQuery, FetchSearchCoursesPageableQueryVariables>({
        query: QUERY_FETCH_SEARCH_COURSE_PREDICT,
        fetchPolicy: 'cache-first',
        variables: {
          page: 0,
          search,
        },
      }),
    ),
    requestHandler(
      apolloClient.query<FetchSearchLatestResourcesPageableQuery, FetchSearchLatestResourcesPageableQueryVariables>({
        query: QUERY_FETCH_RESOURCES_PREDICT,
        fetchPolicy: 'cache-first',
        variables: {
          page: 0,
          search,
        },
      }),
    ),
    requestHandler(
      apolloClient.query<FetchSearchPodcastsPageableQuery, FetchSearchPodcastsPageableQueryVariables>({
        query: QUERY_FETCH_PODCASTS_PREDICT,
        fetchPolicy: 'cache-first',
        variables: {
          page: 0,
          search,
        },
      }),
    ),
  ]);

  return {
    data: [
      ...(coursesResponse?.data?.courses?.data || []),
      ...(resourcesResponse?.data?.resources?.data || []),
      ...(podcastResponse?.data?.podcasts?.data || []),
    ],
  } as SearchPageableResult;
};

export const fetchSearchPage = async () => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<FetchSearchPageQuery, FetchSearchPageQueryVariables>({
      query: QUERY_SEARCH_FETCH_PAGE,
      fetchPolicy: 'cache-first',
    }),
  );

  if (error) {
    console.error('Error fetching podcast page', error);
    return { page: null };
  }

  return {
    page: data?.searchPage?.data?.attributes as SearchPage,
  };
};
